import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, MessageSquare, X } from "lucide-react";
import useAvatarStore from "../stores/avatarStore";

const ChatPanel = ({
  messages,
  input,
  setInput,
  handleSend,
  sending,
  assessing,
  showRoleplaySetup,
  onClose,
}) => {
  const messageEndRef = useRef(null);
  const { coachState } = useAvatarStore();

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const suggestions = [
    "Tell me about safety features",
    "How to handle price objection?",
    "What is the launch timeline?",
  ];

  return (
    <motion.div
      initial={{ x: "100%", opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "100%", opacity: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 25 }}
      className="absolute top-0 right-0 w-full md:w-[420px] h-full bg-white border-l border-gray-200 flex flex-col justify-between z-20 shadow-2xl"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-secondary" />
          <span className="text-sm font-extrabold text-gray-800 tracking-wide">
            Live Chat Feed
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-all"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <Sparkles className="w-8 h-8 text-secondary/40 mb-3 animate-pulse" />
            <p className="text-xs text-gray-500 font-medium">
              Type a response below to start practicing.
            </p>
          </div>
        ) : (
          messages
            .filter((m) => !m.content?.startsWith("__conv_id:"))
            .map((m) => (
              <div
                key={m.id || Math.random().toString()}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-xs shadow-sm border ${
                    m.role === "user"
                      ? "bg-secondary/95 text-white border-secondary/20 rounded-tr-none"
                      : "bg-gray-100/90 text-gray-800 border-gray-200/60 rounded-tl-none"
                  }`}
                >
                  <p className="leading-relaxed whitespace-pre-wrap">
                    {m.content}
                  </p>
                </div>
              </div>
            ))
        )}
        {coachState === "thinking" && (
          <div className="flex justify-start">
            <div className="bg-gray-100/90 border border-gray-200/60 px-4 py-3 rounded-2xl rounded-tl-none flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-gray-450 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-gray-450 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 bg-gray-450 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        <div ref={messageEndRef} />
      </div>

      {/* Suggested prompts & Chat Input */}
      <div className="p-4 border-t border-gray-200/60 bg-gray-50/50">
        {/* Suggested prompts */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-3 mb-1 scrollbar-none">
          {suggestions.map((sug, idx) => (
            <button
              key={idx}
              disabled={sending || assessing || showRoleplaySetup}
              onClick={() => {
                setInput(sug);
              }}
              className="whitespace-nowrap px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-[10px] font-bold text-gray-600 transition-all hover:text-gray-900 hover:border-gray-300 disabled:opacity-50 shadow-sm"
            >
              {sug}
            </button>
          ))}
        </div>

        {/* Dynamic Chat Input */}
        <div className="flex gap-2 items-end w-full">
          <div
            className={`relative flex-1 w-full bg-white rounded-xl border transition-colors flex items-end p-1.5 pr-10 min-h-[40px] ${
              sending || assessing || showRoleplaySetup
                ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-60"
                : "border-gray-300 shadow-sm focus-within:border-secondary/50 focus-within:border-[1.5px]"
            }`}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!sending && !assessing && !showRoleplaySetup && input.trim()) {
                    handleSend();
                  }
                }
              }}
              placeholder="Ask a question or respond…"
              disabled={sending || assessing || showRoleplaySetup}
              rows={1}
              className="w-full text-xs font-normal text-gray-800 bg-transparent outline-none ring-0 border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400 disabled:cursor-not-allowed disabled:text-gray-400 resize-none max-h-24 py-1.5 pl-1.5"
              style={{
                height: "auto",
              }}
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
                }
              }}
            />
            <div className="absolute right-2 bottom-1.5 flex items-center">
              <button
                type="button"
                onClick={() => handleSend()}
                disabled={
                  sending || assessing || !input.trim() || showRoleplaySetup
                }
                className="p-1 bg-secondary hover:bg-secondary-light text-white rounded-lg disabled:opacity-50 transition-all flex items-center justify-center flex-shrink-0 shadow-sm"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ChatPanel;
